/**
=========================================================
* Elite 247 Admin Panel - Modernized Dashboard
=========================================================
*/

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import LinearProgress from "@mui/material/LinearProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Dashboard components
import RecentContent from "layouts/dashboard/components/RecentContent";
import RecentActivity from "layouts/dashboard/components/RecentActivity";

// API
import { adminApi } from "services/api";

// Two stat cards per row on phones. ComplexStatisticsCard is shared and takes no size props,
// so below sm (576px) its padding, icon and title are tightened from here to stop the icon
// squashing and the titles wrapping; from sm up the card renders exactly as designed.
const compactStatCardsSx = ({ breakpoints }) => ({
  [breakpoints.down("sm")]: {
    "& .MuiCard-root > .MuiBox-root": { px: 1.5 },
    "& .MuiCard-root > .MuiBox-root:first-of-type > .MuiBox-root:first-of-type": {
      flexShrink: 0,
      width: "2.75rem",
      height: "2.75rem",
      mt: -2.5,
    },
    "& .MuiCard-root > .MuiBox-root:first-of-type .MuiTypography-button": { fontSize: "0.75rem" },
  },
});

function Dashboard() {
  const [stats, setStats] = useState({
    total: {
      pages: 0,
      services: 0,
      bootcamps: 0,
      blogCategories: 0,
      blogPosts: 0,
      teamMembers: 0,
      testimonials: 0,
      contactSubmissions: 0,
      bootcampApplications: 0,
    },
    published: {
      pages: 0,
      posts: 0,
      bootcamps: 0,
    },
    recent: {
      contactSubmissions: 0,
      bootcampApplications: 0,
    },
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      const data = await adminApi.getStatistics();
      setStats(data);
    } catch (error) {
      console.error("Failed to fetch statistics:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Welcome Header */}
        <MDBox mb={4}>
          <Card
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 8px 32px rgba(102, 126, 234, 0.35)",
              border: "1px solid rgba(255, 255, 255, 0.18)",
              overflow: "visible",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
                pointerEvents: "none",
              },
            }}
          >
            <MDBox
              p={{ xs: 2.5, sm: 3 }}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
            >
              <MDBox minWidth={0}>
                <MDTypography
                  variant="h3"
                  fontWeight="bold"
                  color="white"
                  mb={0.5}
                  sx={{ fontSize: { xs: "1.5rem", sm: "1.875rem" } }}
                >
                  Welcome to Elite 247 Dashboard
                </MDTypography>
                <MDTypography variant="body2" color="white" sx={{ opacity: 0.9 }}>
                  Monitor your platform's performance and manage content efficiently
                </MDTypography>
              </MDBox>
              <MDBox
                sx={{
                  width: { xs: "48px", sm: "70px" },
                  height: { xs: "48px", sm: "70px" },
                  flexShrink: 0,
                  background: "rgba(255, 255, 255, 0.2)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(10px)",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <Icon sx={{ fontSize: 40, color: "white" }}>dashboard</Icon>
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        {/* Loading Bar */}
        {loading && (
          <MDBox mb={2}>
            <LinearProgress
              sx={{
                borderRadius: "4px",
                height: "6px",
                // the theme makes LinearProgress overflow:visible, so the indeterminate bar
                // would slide past the viewport and scroll the page sideways while loading
                overflow: "hidden",
                background: "rgba(102, 126, 234, 0.2)",
                "& .MuiLinearProgress-bar": {
                  background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                },
              }}
            />
          </MDBox>
        )}

        {/* Statistics Cards - Row 1 */}
        <Grid container columnSpacing={{ xs: 2, md: 3 }} rowSpacing={3} sx={compactStatCardsSx}>
          <Grid item xs={6} lg={3}>
            <MDBox mb={1.5}>
              <Tooltip title="Total blog posts in your platform" placement="top">
                <div>
                  <ComplexStatisticsCard
                    color="dark"
                    icon="article"
                    title="Blog Posts"
                    count={loading ? "..." : stats.total.blogPosts}
                    percentage={{
                      color: "info",
                      amount: `${stats.published.posts}`,
                      label: "published",
                    }}
                  />
                </div>
              </Tooltip>
            </MDBox>
          </Grid>
          <Grid item xs={6} lg={3}>
            <MDBox mb={1.5}>
              <Tooltip title="Active bootcamp programs" placement="top">
                <div>
                  <ComplexStatisticsCard
                    color="success"
                    icon="school"
                    title="Bootcamps"
                    count={loading ? "..." : stats.total.bootcamps}
                    percentage={{
                      color: "success",
                      amount: `${stats.published.bootcamps}`,
                      label: "active",
                    }}
                  />
                </div>
              </Tooltip>
            </MDBox>
          </Grid>
          <Grid item xs={6} lg={3}>
            <MDBox mb={1.5}>
              <Tooltip title="Contact form submissions" placement="top">
                <div>
                  <ComplexStatisticsCard
                    color="primary"
                    icon="mail"
                    title="Contact Forms"
                    count={loading ? "..." : stats.total.contactSubmissions}
                    percentage={{
                      color: "success",
                      amount: `+${stats.recent.contactSubmissions}`,
                      label: "this week",
                    }}
                  />
                </div>
              </Tooltip>
            </MDBox>
          </Grid>
          <Grid item xs={6} lg={3}>
            <MDBox mb={1.5}>
              <Tooltip title="Bootcamp applications received" placement="top">
                <div>
                  <ComplexStatisticsCard
                    color="warning"
                    icon="assignment"
                    title="Applications"
                    count={loading ? "..." : stats.total.bootcampApplications}
                    percentage={{
                      color: "success",
                      amount: `+${stats.recent.bootcampApplications}`,
                      label: "this week",
                    }}
                  />
                </div>
              </Tooltip>
            </MDBox>
          </Grid>
        </Grid>

        {/* Statistics Cards - Row 2 */}
        <Grid
          container
          columnSpacing={{ xs: 2, md: 3 }}
          rowSpacing={3}
          mt={1}
          sx={compactStatCardsSx}
        >
          <Grid item xs={6} lg={3}>
            <MDBox mb={1.5}>
              <Tooltip title="IT consulting services offered" placement="top">
                <div>
                  <ComplexStatisticsCard
                    color="info"
                    icon="business"
                    title="Services"
                    count={loading ? "..." : stats.total.services}
                    percentage={{
                      color: "info",
                      amount: "",
                      label: "total services",
                    }}
                  />
                </div>
              </Tooltip>
            </MDBox>
          </Grid>
          <Grid item xs={6} lg={3}>
            <MDBox mb={1.5}>
              <Tooltip title="Website pages and content" placement="top">
                <div>
                  <ComplexStatisticsCard
                    icon="description"
                    title="Pages"
                    count={loading ? "..." : stats.total.pages}
                    percentage={{
                      color: "success",
                      amount: `${stats.published.pages}`,
                      label: "published",
                    }}
                  />
                </div>
              </Tooltip>
            </MDBox>
          </Grid>
          <Grid item xs={6} lg={3}>
            <MDBox mb={1.5}>
              <Tooltip title="Active team members" placement="top">
                <div>
                  <ComplexStatisticsCard
                    color="success"
                    icon="people"
                    title="Team Members"
                    count={loading ? "..." : stats.total.teamMembers}
                    percentage={{
                      color: "info",
                      amount: "",
                      label: "total members",
                    }}
                  />
                </div>
              </Tooltip>
            </MDBox>
          </Grid>
          <Grid item xs={6} lg={3}>
            <MDBox mb={1.5}>
              <Tooltip title="Client testimonials and reviews" placement="top">
                <div>
                  <ComplexStatisticsCard
                    color="warning"
                    icon="star"
                    title="Testimonials"
                    count={loading ? "..." : stats.total.testimonials}
                    percentage={{
                      color: "info",
                      amount: "",
                      label: "total reviews",
                    }}
                  />
                </div>
              </Tooltip>
            </MDBox>
          </Grid>
        </Grid>

        {/* Recent Content & Activity Section */}
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <RecentContent />
            </Grid>
            <Grid item xs={12} lg={4}>
              <RecentActivity />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
